export type CollisionType = "solid" | "launch" | "pickup" | "none";

export type BehaviorId = "solidHit" | "launchJump" | "collect" | "none";

export type ObjectDef = {
  id: string;
  sprites: string[];
  size: { w: number; h: number };
  hitbox: { w: number; h: number };
  collision: CollisionType;
  behavior: BehaviorId;
  zBias: number;
};

const REGISTRY = new Map<string, ObjectDef>();

export function registerObject(def: ObjectDef): void {
  REGISTRY.set(def.id, def);
}

export function getObject(id: string): ObjectDef {
  const def = REGISTRY.get(id);
  if (!def) throw new Error(`Unknown object type: ${id}`);
  return def;
}

export function listObjectIds(): string[] {
  return [...REGISTRY.keys()];
}

export function getRegistry(): ReadonlyMap<string, ObjectDef> {
  return REGISTRY;
}

registerObject({
  id: "tree",
  sprites: ["tree-pine", "tree-hemlock"],
  size: { w: 108, h: 108 },
  hitbox: { w: 36, h: 36 },
  collision: "solid",
  behavior: "solidHit",
  zBias: 8,
});

registerObject({
  id: "rock",
  sprites: ["rock-0", "rock-1", "rock-2", "rock-3"],
  size: { w: 52, h: 48 },
  hitbox: { w: 34, h: 30 },
  collision: "solid",
  behavior: "solidHit",
  zBias: 2,
});

// The box is wider than the hitbox on purpose: the kicker art carries its
// marker flags on the shoulders, outside the launch zone, and the ramp is the
// one object that wants to be seen early at tuck speed. The art is wide (the
// view contains it at its own aspect), so the height here is a bound, not a
// shape.
registerObject({
  id: "jump",
  sprites: ["jump"],
  size: { w: 104, h: 80 },
  hitbox: { w: 70, h: 36 },
  collision: "launch",
  behavior: "launchJump",
  zBias: -4,
});

// The frozen food. Both sprites are square with the food letterboxed inside
// (see tools/art/pickups.cjs), so `size` has to stay square too or the art
// stretches -- the hot dog reads wide because its own art is wide, not because
// the rect is. The hitbox is deliberately generous against a 16px player: a
// collectible that slips past a hair from the middle reads as broken, whereas a
// tree that clips you a hair early reads as your mistake.
registerObject({
  id: "hotdog",
  sprites: ["food-hotdog"],
  size: { w: 62, h: 62 },
  hitbox: { w: 46, h: 46 },
  collision: "pickup",
  behavior: "collect",
  zBias: 0,
});

registerObject({
  id: "burger",
  sprites: ["food-burger"],
  size: { w: 58, h: 58 },
  hitbox: { w: 46, h: 46 },
  collision: "pickup",
  behavior: "collect",
  zBias: 0,
});
